import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";

/**
 * Returns the count of access_requests with status='pending'.
 * Only runs when isAdmin=true (non-admins don't have SELECT on this table).
 * Subscribes to realtime INSERT/UPDATE on access_requests so the count
 * updates automatically when a new request arrives or one is approved.
 */
export function usePendingAccessRequests(isAdmin) {
  const [count, setCount]       = useState(0);
  const [requests, setRequests] = useState([]);
  const channelRef = useRef(null);

  const load = useCallback(async () => {
    if (!isAdmin) { setCount(0); setRequests([]); return; }
    try {
      const { data, error } = await supabase
        .from("access_requests")
        .select("id, name, email")
        .eq("status", "pending")
        .order("created_at", { ascending: false });
      if (!error) {
        setRequests(data ?? []);
        setCount(data?.length ?? 0);
      }
    } catch {
      // Non-admin or network failure — silently keep previous value
    }
  }, [isAdmin]);

  // Initial load
  useEffect(() => { load(); }, [load]);

  // Realtime subscription — mirrors the useMaterialsRealtime pattern
  useEffect(() => {
    if (!isAdmin) return;

    const channel = supabase
      .channel("access_requests_pending_count")
      .on("postgres_changes", {
        event: "*",
        schema: "public",
        table: "access_requests",
      }, load)
      .subscribe();

    channelRef.current = channel;

    return () => {
      channel.unsubscribe();
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [isAdmin, load]);

  return { count, requests };
}
